/**
 * Unified relative-time formatter for the terminal UI.
 *
 * Before this module existed, near-identical relTime() / timeOfDay()
 * helpers were duplicated across TerminalCustomerCard, TerminalWorkspace-
 * Stream, TerminalWorkspaceRail, and TerminalJobWorkspaceModal — each
 * with subtle variations (e.g. "14:22" vs "today 14:22"). Consolidating
 * here guarantees every timestamp in the terminal UI renders the same
 * way, matching the log-file column aesthetic.
 *
 * Exports two formatters with different verbosity levels:
 *
 *   relTime(d)      → compact   ("14:22", "yest", "Tue", "3w", "Oct 4")
 *   relTimeVerbose(d) → verbose ("today 14:22", "yesterday", "Tue", "3w ago", "Oct 4")
 *
 * The compact form is for dense column rendering (thread log, email
 * previews); the verbose form is for status-bar one-liners.
 */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS   = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(d.getDate() + n);
  return r;
}

function hhmm(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Coerce mixed inputs (Date | string | number | null | undefined) to a
 *  valid Date, or return null. Downstream formatters early-return on null. */
function toDate(input: any): Date | null {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Compact relative time. Renders in the narrowest column sensibly —
 * used in the per-email log, thread rail, and card preview.
 *
 *   < 1m       → "now"
 *   < 1h       → "Xm"
 *   today      → "14:22"
 *   yesterday  → "yest"
 *   < 7d       → weekday ("Tue")
 *   < 4w       → "Xw"
 *   this year  → "Oct 4"
 *   prior year → "Oct 4 '24"
 */
export function relTime(input: any): string {
  const d = toDate(input);
  if (!d) return '';
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const min = Math.round(diff / 60000);
  if (min < 1)  return 'now';
  if (min < 60) return `${min}m`;
  if (sameDay(d, now))  return hhmm(d);
  if (sameDay(d, addDays(now, -1))) return 'yest';
  const days = Math.round(diff / 86400000);
  if (days < 7)  return WEEKDAYS[d.getDay()];
  if (days < 28) return `${Math.round(days / 7)}w`;
  if (d.getFullYear() === now.getFullYear()) {
    return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
  }
  return `${MONTHS[d.getMonth()]} ${d.getDate()} '${String(d.getFullYear()).slice(-2)}`;
}

/**
 * Verbose relative time. Same breakpoints as relTime() but with the
 * "today" / "yesterday" words spelled out and minute/hour counts
 * carrying an explicit "ago" suffix. Used in status-bar summaries
 * where the extra context is readable without crowding.
 *
 *   < 1m  → "now"
 *   < 1h  → "Xm ago"
 *   today → "today 14:22"
 *   yest. → "yesterday"
 *   < 7d  → weekday
 *   < 4w  → "Xw ago"
 *   else  → "Oct 4" (same-year) / "Oct 4 '24" (prior)
 */
export function relTimeVerbose(input: any): string {
  const d = toDate(input);
  if (!d) return '';
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const min = Math.round(diff / 60000);
  if (min < 1)  return 'now';
  if (min < 60) return `${min}m ago`;
  if (sameDay(d, now))  return `today ${hhmm(d)}`;
  if (sameDay(d, addDays(now, -1))) return 'yesterday';
  const days = Math.round(diff / 86400000);
  if (days < 7)  return WEEKDAYS[d.getDay()];
  if (days < 28) return `${Math.round(days / 7)}w ago`;
  if (d.getFullYear() === now.getFullYear()) {
    return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
  }
  return `${MONTHS[d.getMonth()]} ${d.getDate()} '${String(d.getFullYear()).slice(-2)}`;
}
